import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';
import { AuditLogEntry, AuditLogListResponse, Project } from '../types/api';
import Logger from '../utils/logger';
import {
  ClipboardDocumentListIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

type TabKey = 'all' | 'me';

const PAGE_SIZE = 30;

const ACTION_GROUP_VALUES = [
  { key: 'all', value: '' },
  { key: 'deployments', value: 'deployment.*' },
  { key: 'builds', value: 'build.*' },
  { key: 'imports', value: 'import.*' },
  { key: 'projects', value: 'project.*' },
  { key: 'environments', value: 'environment.*' },
  { key: 'backups', value: 'backup.*' },
  { key: 'sql', value: 'sql.*' },
  { key: 'organisation', value: 'organisation.*' },
];

const RESOURCE_TYPE_VALUES = [
  { key: 'all', value: '' },
  { key: 'deployment', value: 'deployment' },
  { key: 'build', value: 'build' },
  { key: 'import', value: 'import' },
  { key: 'project', value: 'project' },
  { key: 'environment', value: 'environment' },
  { key: 'backup', value: 'backup' },
  { key: 'sql', value: 'sql' },
  { key: 'organisation', value: 'organisation' },
];

const AuditLogsPage: React.FC = () => {
  const { t } = (useTranslation as any)('audit');
  const [tab, setTab] = useState<TabKey>('all');
  const [data, setData] = useState<AuditLogListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [page, setPage] = useState(1);

  // Filters
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedResourceType, setSelectedResourceType] = useState('');
  const [selectedSuccess, setSelectedSuccess] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    apiService.getProjects().then(setProjects).catch(() => {});
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const filters: Record<string, any> = {
        page,
        page_size: PAGE_SIZE,
      };
      if (selectedProjectId) filters.project_id = selectedProjectId;
      if (selectedAction) filters.action = selectedAction;
      if (selectedResourceType) filters.resource_type = selectedResourceType;
      if (selectedSuccess !== '') filters.success = selectedSuccess === 'true';
      if (dateFrom) filters.date_from = new Date(dateFrom).toISOString();
      if (dateTo) filters.date_to = new Date(dateTo + 'T23:59:59').toISOString();

      const response = tab === 'me'
        ? await apiService.getMyAuditLogs(filters)
        : await apiService.getAuditLogs(filters);

      setData(response);
    } catch (error: any) {
      Logger.error('Error loading audit logs:', error);
      toast.error(t('loadError'));
    } finally {
      setLoading(false);
    }
  }, [tab, page, selectedProjectId, selectedAction, selectedResourceType, selectedSuccess, dateFrom, dateTo]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Reset page on filter or tab change
  useEffect(() => {
    setPage(1);
  }, [tab, selectedProjectId, selectedAction, selectedResourceType, selectedSuccess, dateFrom, dateTo]);

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  const getActionBadgeColor = (action: string) => {
    if (action.includes('create')) return 'bg-green-500/15 text-green-400 border-green-500/20';
    if (action.includes('delete') || action.includes('remove')) return 'bg-red-500/15 text-red-400 border-red-500/20';
    if (action.includes('update') || action.includes('change')) return 'bg-blue-500/15 text-blue-400 border-blue-500/20';
    if (action.includes('trigger') || action.includes('restart') || action.includes('execute')) return 'bg-orange-500/15 text-orange-400 border-orange-500/20';
    return 'bg-surface-3 text-text-secondary border-border-1';
  };

  const clearFilters = () => {
    setSelectedProjectId('');
    setSelectedAction('');
    setSelectedResourceType('');
    setSelectedSuccess('');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = selectedProjectId || selectedAction || selectedResourceType || selectedSuccess !== '' || dateFrom || dateTo;

  const getResourceName = (log: AuditLogEntry): string | null => {
    if (!log.details) return null;
    return log.details.name || log.details.repo || log.details.repository || log.details.app_name || log.details.email || log.details.source_image || null;
  };

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center">
            <ClipboardDocumentListIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-text-primary">{t('title')}</h1>
            <p className="text-sm text-text-tertiary">
              {t('subtitle')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <span className="text-sm text-text-tertiary">
              {t('actionCount', { count: data.total })}
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-surface-1 rounded-lg p-1 w-fit border border-border-0">
        <button
          onClick={() => setTab('all')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
            tab === 'all'
              ? 'bg-blue-500/15 text-blue-400'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-3'
          }`}
        >
          {t('tabs.all')}
        </button>
        <button
          onClick={() => setTab('me')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
            tab === 'me'
              ? 'bg-blue-500/15 text-blue-400'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-3'
          }`}
        >
          <UserIcon className="w-3.5 h-3.5" />
          {t('tabs.me')}
        </button>
      </div>

      {/* Filters toggle */}
      <div className="mb-4">
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
            hasActiveFilters
              ? 'bg-blue-500/10 border-blue-500/30 text-blue-400'
              : 'bg-surface-1 border-border-0 text-text-secondary hover:text-text-primary'
          }`}
        >
          <FunnelIcon className="w-4 h-4" />
          {t('filters.label')}
          {hasActiveFilters && (
            <span className="w-1.5 h-1.5 rounded-full bg-accent-400" />
          )}
        </button>
      </div>

      {/* Filter panel */}
      {filtersOpen && (
        <div className="mb-4 p-4 bg-surface-1 border border-border-0 rounded-xl space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* Project filter - hide in 'me' tab */}
            {tab === 'all' && (
              <div>
                <label className="block text-xs text-text-tertiary mb-1">{t('filters.project')}</label>
                <select
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  className="w-full bg-surface-2 border border-border-1 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500"
                >
                  <option value="">{t('filters.allProjects')}</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.nom}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Action filter */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1">{t('filters.action')}</label>
              <select
                value={selectedAction}
                onChange={(e) => setSelectedAction(e.target.value)}
                className="w-full bg-surface-2 border border-border-1 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500"
              >
                {ACTION_GROUP_VALUES.map((a) => (
                  <option key={a.value} value={a.value}>{t(`actionGroups.${a.key}`)}</option>
                ))}
              </select>
            </div>

            {/* Resource type filter */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1">{t('filters.resourceType')}</label>
              <select
                value={selectedResourceType}
                onChange={(e) => setSelectedResourceType(e.target.value)}
                className="w-full bg-surface-2 border border-border-1 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500"
              >
                {RESOURCE_TYPE_VALUES.map((r) => (
                  <option key={r.value} value={r.value}>{r.key === 'all' ? t('resourceTypes.all') : r.key}</option>
                ))}
              </select>
            </div>

            {/* Success filter */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1">{t('filters.status')}</label>
              <select
                value={selectedSuccess}
                onChange={(e) => setSelectedSuccess(e.target.value)}
                className="w-full bg-surface-2 border border-border-1 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500"
              >
                <option value="">{t('filters.all')}</option>
                <option value="true">{t('filters.success')}</option>
                <option value="false">{t('filters.failure')}</option>
              </select>
            </div>

            {/* Date from */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1">{t('filters.dateFrom')}</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full bg-surface-2 border border-border-1 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500"
              />
            </div>

            {/* Date to */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1">{t('filters.dateTo')}</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full bg-surface-2 border border-border-1 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500"
              />
            </div>
          </div>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-text-tertiary hover:text-text-primary underline"
            >
              {t('filters.clearFilters')}
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-surface-1 border border-border-0 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent-500 border-t-transparent" />
          </div>
        ) : !data || data.logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-text-tertiary">
            <ClipboardDocumentListIcon className="w-12 h-12 mb-3 opacity-40" />
            <p className="text-sm">{t('empty')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-0 bg-surface-2/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wide">{t('table.date')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wide">{t('table.user')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wide">{t('table.action')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wide">{t('table.resource')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wide">{t('table.status')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wide hidden lg:table-cell">{t('table.ip')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-0">
                {data.logs.map((log: AuditLogEntry) => (
                  <tr key={log.id} className="hover:bg-surface-2/30 transition-colors">
                    <td className="px-4 py-3 text-text-secondary whitespace-nowrap text-xs">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-text-primary text-xs font-medium truncate max-w-[180px]">
                          {log.user_name || t('table.unknownUser')}
                        </span>
                        {log.user_email && (
                          <span className="text-text-tertiary text-[11px] truncate max-w-[180px]">
                            {log.user_email}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium border ${getActionBadgeColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-text-primary text-xs">{log.resource_type}</span>
                        {getResourceName(log) && (
                          <span className="text-text-secondary text-[11px] truncate max-w-[200px]" title={getResourceName(log) || undefined}>
                            {getResourceName(log)}
                          </span>
                        )}
                        {log.resource_id && (
                          <span className="text-text-tertiary text-[11px] font-mono truncate max-w-[160px]">
                            {log.resource_id}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {log.success ? (
                        <span className="flex items-center gap-1 text-green-400 text-xs">
                          <CheckCircleIcon className="w-4 h-4" />
                          {t('table.ok')}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-400 text-xs" title={log.error_message || undefined}>
                          <XCircleIcon className="w-4 h-4" />
                          {t('table.failure')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-text-tertiary text-xs font-mono hidden lg:table-cell">
                      {log.ip_address || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {data && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border-0">
            <span className="text-xs text-text-tertiary">
              {t('page', { current: page, total: totalPages })}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded-lg border border-border-1 text-text-secondary hover:bg-surface-3 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="p-1.5 rounded-lg border border-border-1 text-text-secondary hover:bg-surface-3 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogsPage;
